import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Grid, Chip, Tab, Tabs, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, IconButton, TextField, InputAdornment, Button,
  Divider, useTheme, useMediaQuery, Menu, MenuItem, Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate } from "react-router-dom";
import { baseapi } from "../Environmentalurl";
import { entity_id, Platform } from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";
import MerchantTransactions from "./Merchanttransactions";
import MerchantSettlement from "./Merchantsettlement";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';


// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  primary: "#0F172A",   // near-black — authority
  accent: "#6366F1",   // indigo — merchant identity
  accentSoft: "#EEF2FF",
  green: "#059669",
  greenSoft: "#D1FAE5",
  amber: "#D97706",
  amberSoft: "#FEF3C7",
  red: "#DC2626",
  redSoft: "#FEE2E2",
  surface: "#FFFFFF",
  bg: "#F8FAFC",
  border: "#E2E8F0",
  muted: "#64748B",
  text: "#1E293B",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `₹${parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusChip = (status) => {
  const map = {
    SUCCESS: { label: "Success", bg: C.greenSoft, color: C.green },
    SETTLED: { label: "Settled", bg: C.greenSoft, color: C.green },
    PENDING: { label: "Pending", bg: C.amberSoft, color: C.amber },
    UNSETTLED: { label: "Unsettled", bg: C.amberSoft, color: C.amber },
    FAILED: { label: "Failed", bg: C.redSoft, color: C.red },
    REFUNDED: { label: "Refunded", bg: "#EDE9FE", color: "#7C3AED" },
  };
  const s = map[status?.toUpperCase()] || { label: status, bg: C.accentSoft, color: C.accent };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        background: s.bg,
        color: s.color,
        fontWeight: 700,
        fontSize: 11,
        height: 22,
        borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon, color, soft, trend }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "16px",
      p: 2.5,
      background: C.surface,
      height: "100%",
      position: "relative",
      overflow: "hidden",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      },
    }}
  >
    {/* Accent corner */}
    <Box sx={{ position: "absolute", top: 0, right: 0, width: 80, height: 80, borderRadius: "0 16px 0 80px", background: soft, opacity: 0.6 }} />

    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
      <Box
        sx={{
          width: 44, height: 44, borderRadius: "12px",
          background: soft,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
      </Box>
      {trend !== undefined && (
        <Box display="flex" alignItems="center" gap={0.3}>
          {trend >= 0
            ? <ArrowUpwardIcon sx={{ fontSize: 14, color: C.green }} />
            : <ArrowDownwardIcon sx={{ fontSize: 14, color: C.red }} />}
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: trend >= 0 ? C.green : C.red }}>
            {Math.abs(trend)}%
          </Typography>
        </Box>
      )}
    </Box>

    <Typography sx={{ fontSize: 26, fontWeight: 800, color: C.text, letterSpacing: -0.5, mb: 0.3 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.muted, mb: 0.3 }}>{title}</Typography>
    {subtitle && (
      <Typography sx={{ fontSize: 11, color: C.muted }}>{subtitle}</Typography>
    )}
  </Card>
);

// ── Main Component ─────────────────────────────────────────────────────────────
export default function MerchantDashboard() {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const ip = useIPAddress();

  const merchantName = sessionStorage.getItem("MerchantName") || "Merchant";
  const mobile = sessionStorage.getItem("mobileNumber") || "";
  const sessionId = JSON.parse(sessionStorage.getItem("SessionID") || "null") || "";

  const [tab, setTab] = useState(0); // 0=Transactions, 1=Settlements
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterEl, setFilterEl] = useState(null);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);

  // ── Summary — read directly from sessionStorage set during login (5555 response) ──
  const [summary, setSummary] = useState(() => ({
    settledAmount: sessionStorage.getItem("merchantSettledAmount") || "0.00",
    unsettledAmount: sessionStorage.getItem("merchantUnsettledAmount") || "0.00",
  }));

  // Refresh button re-reads sessionStorage (in case of page reload)
  const refreshSummary = () => setSummary({
    settledAmount: sessionStorage.getItem("merchantSettledAmount") || "0.00",
    unsettledAmount: sessionStorage.getItem("merchantUnsettledAmount") || "0.00",
  });

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(false);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();

    localStorage.removeItem("SessionWalletToken");

    localStorage.removeItem("token"); 
    sessionStorage.removeItem("token");

    navigate("/", { replace: true });
  };

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", background: C.bg, fontFamily: "'Inter', 'Calibri', sans-serif" }}>

      {/* ── Top Nav ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "20px",
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        {/* Left */}
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src="/Assets/ta_walletlogo.png"
            alt="TA"
            style={{ width: 52, height: 52 }}
          />

          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 800,
              color: "#16A34A",
              lineHeight: 1.1,
            }}
          >
            ONE
            <br />
            APP
          </Typography>
        </Box>

        {/* Center Search */}
        <TextField
          placeholder="Search"
          size="small"
          sx={{
            width: 420,
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
              background: "#F8FAFC",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Right */}
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            sx={{
              background: "#F3F4F6",
              width: 42,
              height: 42,
            }}
          >
            <NotificationsNoneIcon />
          </IconButton>

          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: "1px solid #E5E7EB",
              borderRadius: "30px",
              px: 1.5,
              py: 0.5,
              cursor: "pointer",
              "&:hover": {
                background: "#F8FAFC",
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#16A34A",
                width: 38,
                height: 38,
              }}
            >
              {merchantName[0]}
            </Avatar>

            <Typography fontWeight={700}>
              {merchantName}
            </Typography>

            <KeyboardArrowDownIcon />
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "12px",
                minWidth: 170,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleLogout();
              }}
            >
              <LogoutIcon
                sx={{
                  mr: 1,
                  color: "#DC2626",
                }}
              />

              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Page Content ─────────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1280, mx: "auto" }}>

        {/* Page title */}
        <Box mb={3}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.text, mb: 0.3 }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted }}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </Typography>
        </Box>

        {/* ── Stat Cards — values from login (5555) response ──────────────────── */}
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
            <StatCard
              title="Settled Amount"
              value={fmt(summary.settledAmount)}
              icon={<AccountBalanceIcon />}
              color={C.green}
              soft={C.greenSoft}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
            <StatCard
              title="Unsettled Amount"
              value={fmt(summary.unsettledAmount)}
              icon={<PendingActionsIcon />}
              color={C.amber}
              soft={C.amberSoft}
            />
          </Grid>
        </Grid>

        {/* ── Settled vs Unsettled Bar ────────────────────────────────────────── */}
        <Card elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "16px", p: 2.5, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Settlement Overview
            </Typography>
            <Box display="flex" gap={2}>
              <Box display="flex" alignItems="center" gap={0.7}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: C.green }} />
                <Typography sx={{ fontSize: 12, color: C.muted }}>Settled</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.7}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: C.amber }} />
                <Typography sx={{ fontSize: 12, color: C.muted }}>Unsettled</Typography>
              </Box>
            </Box>
          </Box>

          {/* Progress bar */}
          {(() => {
            const total = parseFloat(summary.settledAmount) + parseFloat(summary.unsettledAmount);
            const settledPct = total > 0 ? (parseFloat(summary.settledAmount) / total) * 100 : 0;
            return (
              <Box>
                <Box
                  sx={{
                    height: 12, borderRadius: "6px",
                    background: C.amberSoft,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${settledPct}%`,
                      background: `linear-gradient(90deg, ${C.green}, #10B981)`,
                      borderRadius: "6px",
                      transition: "width 1s ease",
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography sx={{ fontSize: 12, color: C.green, fontWeight: 600 }}>
                    {fmt(summary.settledAmount)} ({settledPct.toFixed(1)}%)
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: C.amber, fontWeight: 600 }}>
                    {fmt(summary.unsettledAmount)} ({(100 - settledPct).toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Card>

        {/* ── Transactions / Settlements Tabs ─────────────────────────────── */}
        <Card elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "16px", overflow: "hidden" }}>

          {/* Tab header */}
          <Box sx={{ px: 3, pt: 2.5, pb: 0, borderBottom: `1px solid ${C.border}` }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  textTransform: "none", fontWeight: 600, fontSize: 13,
                  minHeight: 36, px: 2, color: C.muted,
                },
                "& .Mui-selected": { color: C.accent },
                "& .MuiTabs-indicator": { background: C.accent, borderRadius: "2px" },
              }}
            >
              <Tab label="Transactions" />
              <Tab label="Settlements" />
            </Tabs>
          </Box>

          {/* Tab 0 — Transactions component */}
          {tab === 0 && (
            <Box sx={{ p: 2.5 }}>
              <MerchantTransactions />
            </Box>
          )}

          {/* Tab 1 — Settlements component */}
          {tab === 1 && (
            <Box sx={{ p: 2.5 }}>
              <MerchantSettlement />
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
}